import { Fragment as _Fragment, jsx as _jsx } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useDataPersistence } from '../../hooks/useDataPersistence';
import { useNavigationRefresh } from '../../hooks/useNavigationRefresh';
import { dataSyncValidator } from '../../utils/dataSyncValidator';
import supabase, { getSessionSafe } from '../../utils/supabase-client';
import { setSessionFromLogin } from '../../utils/axios-auth';
import { debugSessionLog } from '../../utils/debug-session-log';
/**
 * Persistent Data Provider
 *
 * Provides persistent data management and ensures data consistency
 * across the application. This component handles initial data loading,
 * Option B listener for stale-data refetch, and context for data persistence operations.
 */
const LOG_PREFIX = '[PersistentDataProvider]';
/** Ensure axios-auth session cache is populated before role-based API batch (avoids 401 when Redux has no token yet but Supabase session exists). */
async function warmAxiosSessionCache(reduxSession) {
    // Use getSessionSafe() instead of supabase.auth.getSession() directly. The
    // direct call can hang indefinitely under navigator.locks contention,
    // blocking the entire data-load pipeline (root cause of "data only appears
    // after refresh"). getSessionSafe races against a 3s timeout and falls
    // back to localStorage tokens so the role-based batch always proceeds.
    const session = await getSessionSafe();
    if (session?.access_token) {
        setSessionFromLogin({
            access_token: session.access_token,
            refresh_token: session.refresh_token ?? '',
        });
    }
    else if (reduxSession?.access_token) {
        setSessionFromLogin({
            access_token: reduxSession.access_token,
            refresh_token: reduxSession.refresh_token ?? '',
        });
    }
}
export const PersistentDataProvider = ({ children }) => {
    const { user, isAuthenticated, session: reduxSession } = useSelector((state) => state.auth);
    const { refreshRoleBasedData, refreshUserData, refreshNotes, refreshLessons, refreshQuizzes, refreshSchedule, refreshPayments, refreshGamification, } = useDataPersistence();
    const isMounted = useRef(true);
    const hasRunInitialProfileRefresh = useRef(false);
    const [hasSessionFromSupabase, setHasSessionFromSupabase] = useState(false);
    // Derive hasSession: true if we have session from Supabase OR from Redux (login.fulfilled sets it).
    // This fixes first-sign-in race where Redux has session before Supabase getSession() resolves.
    const hasSession = hasSessionFromSupabase || !!(isAuthenticated && reduxSession?.access_token);
    // Enable navigation-aware data refresh
    useNavigationRefresh();
    // Wait for Supabase session and listen for auth changes.
    // Redux session (from login.fulfilled) is also accepted via hasSession derivation above.
    useEffect(() => {
        let cancelled = false;
        const checkSession = async () => {
            // Use timeout-safe wrapper so a wedged navigator.locks lock cannot
            // block the entire data-load pipeline (in that case we fall back to
            // localStorage tokens and `hasSession` flips to true via Redux side).
            const session = await getSessionSafe();
            console.log(`${LOG_PREFIX} getSession:`, session ? 'found' : 'none');
            if (!cancelled && session) {
                setHasSessionFromSupabase(true);
            }
        };
        checkSession();
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (!cancelled) {
                console.log(`${LOG_PREFIX} authStateChange: ${event}, session=${!!session}`);
                setHasSessionFromSupabase(!!session);
                // Keep axios session cache aligned with Supabase immediately after refresh (avoids 401 from stale Bearer token during batched loads)
                if (session?.access_token && (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN')) {
                    setSessionFromLogin({
                        access_token: session.access_token,
                        refresh_token: session.refresh_token ?? '',
                    });
                }
            }
        });
        return () => {
            cancelled = true;
            subscription.unsubscribe();
        };
    }, []);
    // Option B listener: when dataSyncValidator reports stale + needsRefresh, dispatch the right refetch.
    // On mount, addListener triggers a flush of any queued stale types (writes that occurred while
    // unmounted), so handleStaleResult runs immediately for those and refetches run.
    useEffect(() => {
        isMounted.current = true;
        const handleStaleResult = (result) => {
            if (!isMounted.current || !user?.id || !hasSession)
                return;
            const stale = result.staleData?.filter((s) => s.needsRefresh) ?? [];
            if (stale.length === 0)
                return;
            const logRefetchFailure = (dataType, err) => {
                console.warn(`[PersistentDataProvider] Refetch after stale (${dataType}) failed:`, err);
            };
            stale.forEach((s) => {
                const dataType = s.dataType;
                if (dataType === 'auth')
                    refreshUserData(true).catch((e) => logRefetchFailure('auth', e));
                else if (dataType === 'notes')
                    refreshNotes(true).catch((e) => logRefetchFailure('notes', e));
                else if (dataType === 'lessons')
                    refreshLessons(true).catch((e) => logRefetchFailure('lessons', e));
                else if (dataType === 'quiz')
                    refreshQuizzes(true).catch((e) => logRefetchFailure('quiz', e));
                else if (dataType === 'schedule')
                    refreshSchedule().catch((e) => logRefetchFailure('schedule', e));
                else if (dataType === 'payments')
                    refreshPayments(true).catch((e) => logRefetchFailure('payments', e));
                else if (dataType === 'gamification')
                    refreshGamification().catch((e) => logRefetchFailure('gamification', e));
            });
        };
        dataSyncValidator.addListener(handleStaleResult);
        return () => {
            isMounted.current = false;
            dataSyncValidator.removeListener(handleStaleResult);
        };
    }, [
        user?.id,
        hasSession,
        refreshUserData,
        refreshNotes,
        refreshLessons,
        refreshQuizzes,
        refreshSchedule,
        refreshPayments,
        refreshGamification,
    ]);
    // Log data load conditions when they change (always, helps diagnose first-sign-in issues in browser console)
    useEffect(() => {
        const willLoadData = isAuthenticated && !!user?.id && hasSession;
        const sessionSource = hasSessionFromSupabase ? 'supabase' : (reduxSession?.access_token ? 'redux' : 'none');
        console.log(`${LOG_PREFIX} conditions:`, {
            auth: isAuthenticated,
            uid: user?.id?.slice(0, 8),
            session: hasSession,
            src: sessionSource,
            willLoad: willLoadData,
        });
    }, [isAuthenticated, user?.id, hasSession, hasSessionFromSupabase, reduxSession?.access_token]);
    // Once per session: force profile refresh so persisted state is corrected by server (Plan 4.2)
    useEffect(() => {
        if (!isAuthenticated || !user?.id || !hasSession || hasRunInitialProfileRefresh.current)
            return;
        hasRunInitialProfileRefresh.current = true;
        void (async () => {
            try {
                await warmAxiosSessionCache(reduxSession);
                console.log(`${LOG_PREFIX} initial profile refresh for:`, user?.id?.slice(0, 8));
                await refreshUserData(true);
            }
            catch (error) {
                console.error(`${LOG_PREFIX} Error on initial profile refresh:`, error);
            }
        })();
    }, [isAuthenticated, user?.id, hasSession, refreshUserData, reduxSession?.access_token, reduxSession?.refresh_token]);
    // Initialize data when user is authenticated and session is established (Supabase or Redux)
    useEffect(() => {
        if (!(isAuthenticated && user?.id && hasSession)) {
            if (isAuthenticated && user?.id && !hasSession) {
                console.warn(`${LOG_PREFIX} Auth OK but NO SESSION — data will NOT load. Redux session:`, !!reduxSession?.access_token, 'Supabase session:', hasSessionFromSupabase);
                // #region agent log
                debugSessionLog({ runId: 'pre-fix', hypothesisId: 'H1', location: 'PersistentDataProvider.tsx:dataLoad:skipped', message: 'authenticated but hasSession false — role batch skipped', data: { uidPrefix: user?.id?.slice(0, 8) ?? null, reduxToken: !!reduxSession?.access_token, hasSessionFromSupabase } });
                // #endregion
            }
            return;
        }
        void (async () => {
            try {
                await warmAxiosSessionCache(reduxSession);
                console.log(`${LOG_PREFIX} LOADING DATA for:`, user?.id?.slice(0, 8), 'role:', user?.role);
                await refreshRoleBasedData();
                // #region agent log
                debugSessionLog({ runId: 'pre-fix', hypothesisId: 'H4', location: 'PersistentDataProvider.tsx:dataLoad:ok', message: 'refreshRoleBasedData completed', data: { uidPrefix: user?.id?.slice(0, 8) ?? null, role: user?.role ?? null } });
                // #endregion
            }
            catch (error) {
                console.error(`${LOG_PREFIX} Error initializing persistent data:`, error);
                // #region agent log
                debugSessionLog({ runId: 'pre-fix', hypothesisId: 'H4', location: 'PersistentDataProvider.tsx:dataLoad:error', message: 'refreshRoleBasedData failed', data: { err: String(error?.message ?? error).slice(0, 120) } });
                // #endregion
            }
        })();
    }, [isAuthenticated, user?.id, hasSession, refreshRoleBasedData, reduxSession?.access_token, reduxSession?.refresh_token, hasSessionFromSupabase]);
    return _jsx(_Fragment, { children: children });
};
